import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import type { Option } from 'types/entity'
import { toLegacyDateTime } from './ContactCheckboxes'

interface DealFormProps {
  entityId: string
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/** Port of the legacy Add Deal action (IMSService/SaveDealData). Lead only. */
export function DealForm({ entityId, onSuccess }: DealFormProps) {
  const [pipelines, setPipelines] = useState<Option[]>([])
  const [stages, setStages] = useState<Option[]>([])
  const [lostReasons, setLostReasons] = useState<Option[]>([])
  const [users, setUsers] = useState<Option[]>([])

  const [pipelineId, setPipelineId] = useState<string>('')
  const [stageId, setStageId] = useState<string>('')
  const [lostReasonId, setLostReasonId] = useState<string>('')
  const [amount, setAmount] = useState<string>('')
  const [closingDate, setClosingDate] = useState<string>('')
  const [dealOwner, setDealOwner] = useState<string>('')
  const [probability, setProbability] = useState<string>('50')

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadData() {
      try {
        const [pls, usrs] = await Promise.all([api.fetchDealPipelines(), api.fetchAssignedUsers()])
        if (!active) return
        setPipelines(pls)
        setUsers(usrs)
        // Default the owner to the current user, as the legacy form did.
        const { userId } = api.getSessionContext()
        const current = usrs.find((u) => String(u.id) === String(userId))
        setDealOwner(current?.id ?? (usrs[0]?.id ?? ''))
      } catch {
        if (active) setError('Failed to load deal pipelines.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadData()
    return () => {
      active = false
    }
  }, [])

  /** Pipeline change reloads the stage list (legacy cascade). */
  const handlePipelineChange = async (value: string) => {
    setPipelineId(value)
    setStageId('')
    setLostReasonId('')
    setStages([])
    setLostReasons([])
    if (!value) return
    try {
      setStages(await api.fetchDealPipelineStages(value))
    } catch {
      setError('Failed to load pipeline stages.')
    }
  }

  const selectedStage = stages.find((s) => s.id === stageId)
  const isLostStage = /lost/i.test(selectedStage?.name ?? '')

  /** A "lost" stage also loads the lost-reason list (UI parity; not sent). */
  const handleStageChange = async (value: string) => {
    setStageId(value)
    setLostReasonId('')
    const stage = stages.find((s) => s.id === value)
    if (stage && /lost/i.test(stage.name) && pipelineId) {
      try {
        setLostReasons(await api.fetchDealStageReasons(pipelineId))
      } catch {
        setLostReasons([])
      }
    }
  }

  const handleReset = () => {
    setPipelineId('')
    setStageId('')
    setLostReasonId('')
    setStages([])
    setLostReasons([])
    setAmount('')
    setClosingDate('')
    setProbability('50')
    const { userId } = api.getSessionContext()
    const current = users.find((u) => String(u.id) === String(userId))
    setDealOwner(current?.id ?? (users[0]?.id ?? ''))
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pipelineId) {
      setError('Please select a pipeline.')
      return
    }
    if (!stageId) {
      setError('Please select a stage.')
      return
    }
    if (!(Number(amount) > 0)) {
      setError('Amount must be greater than zero.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const msg = await api.submitActionDeal({
        entityId,
        pipelineId,
        stageId,
        amount: Number(amount),
        closingDate: toLegacyDateTime(closingDate),
        dealOwner,
        probability: Math.min(100, Math.max(0, Number(probability) || 0)),
      })
      onSuccess?.(msg)
    } catch (err) {
      setError((err as Error).message || 'Failed to create deal.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading Deal settings…</div>

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="dl-pipeline">
          Pipeline <span className="req">*</span>
        </label>
        <select
          id="dl-pipeline"
          className="form-control"
          value={pipelineId}
          onChange={(e) => void handlePipelineChange(e.target.value)}
        >
          <option value="">-- Select Pipeline --</option>
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="dl-stage">
          Stage <span className="req">*</span>
        </label>
        <select
          id="dl-stage"
          className="form-control"
          value={stageId}
          disabled={!pipelineId}
          onChange={(e) => void handleStageChange(e.target.value)}
        >
          <option value="">-- Select Stage --</option>
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {isLostStage && (
        <div className="form-group">
          <label className="form-label" htmlFor="dl-lostreason">
            Lost Reason
          </label>
          <select
            id="dl-lostreason"
            className="form-control"
            value={lostReasonId}
            onChange={(e) => setLostReasonId(e.target.value)}
          >
            <option value="">-- Select Reason --</option>
            {lostReasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="form-label" htmlFor="dl-amount">
          Amount <span className="req">*</span>
        </label>
        <input
          id="dl-amount"
          type="number"
          className="form-control"
          placeholder="Deal amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="dl-closing">
          Closing Date
        </label>
        <input
          id="dl-closing"
          type="date"
          className="form-control"
          value={closingDate}
          onChange={(e) => setClosingDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="dl-owner">
          Deal Owner
        </label>
        <select
          id="dl-owner"
          className="form-control"
          value={dealOwner}
          onChange={(e) => setDealOwner(e.target.value)}
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="dl-probability">
          Probability (%)
        </label>
        <input
          id="dl-probability"
          type="number"
          className="form-control"
          min={0}
          max={100}
          value={probability}
          onChange={(e) => setProbability(e.target.value)}
        />
      </div>

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : 'Add Deal'}
        </button>
      </div>
    </div>
  )
}
